import { useEffect, useSyncExternalStore } from 'react';
import {
  ConnectivityStatus,
  getSnapshot,
  setOnline,
  subscribe,
} from '../utils/connectivityStore';

export interface Connectivity {
  online: boolean;
  serverReachable: boolean;
  status: ConnectivityStatus;
  /** Convenience: anything other than `online` means the app should warn. */
  disconnected: boolean;
}

function mapState(snapshot: ReturnType<typeof getSnapshot>): Connectivity {
  return {
    online: snapshot.online,
    serverReachable: snapshot.serverReachable,
    status: snapshot.status,
    disconnected: snapshot.status !== 'online',
  };
}

/**
 * Single source of truth for app-wide connectivity. Combines the browser's
 * online/offline signal with real API-call outcomes reported by the axios
 * client. Use this to drive a persistent banner, guard mutations, or branch
 * list states into "offline" / "server unavailable" variants.
 */
export function useConnectivity(): Connectivity {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  // Track browser online/offline events and feed them into the store. Done here
  // (in the React layer) so the store stays free of DOM/window globals and easy
  // to test, and so listeners attach only while the app is mounted.
  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    // Sync in case the device went offline before this hook mounted.
    setOnline(navigator.onLine);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return mapState(snapshot);
}
